(() => {
  if (window.customElements.get('ecomhard-kits')) return;

  class EcomhardKits extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.currency = this.dataset.currency || 'BRL';
      this.sizeLabels = (this.dataset.sizeLabels || 'Tamanho,Size')
        .split(',')
        .map((label) => this.normalize(label))
        .filter(Boolean);
      this.steps = Array.from(this.querySelectorAll('[data-kit-step]'));
      this.currentStep = 0;
      this.pricing = document.querySelector(`[data-ecomhard-kit-pricing="${this.id}"]`);
      this.sectionId = this.closest('.shopify-section')?.id?.replace(/^shopify-section-/, '') || '';
      this.galleryCache = new Map();
      this.galleryRequestId = 0;
      this.galleryWasSynced = false;
      this.isTransitioning = false;
      this.productOrderByGroup = new Map();

      this.steps.forEach((step, index) => this.initializeStep(step, index));
      this.bindNavigation();
      this.showStep(0, false);
      this.updateKitPricing();
    }

    normalize(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();
    }

    parseNumber(value) {
      if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
      const normalized = String(value || '')
        .trim()
        .replace(/\s/g, '')
        .replace(/\.(?=\d{3}(?:\D|$))/g, '')
        .replace(',', '.')
        .replace(/[^0-9.-]/g, '');
      const parsed = Number.parseFloat(normalized);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    parseStepData(step) {
      try {
        const raw = step.querySelector('[data-kit-step-data]')?.textContent || '{}';
        const data = JSON.parse(raw);
        const unique = new Map();
        (data.products || []).forEach((product) => {
          if (product && product.id && !unique.has(String(product.id))) {
            unique.set(String(product.id), product);
          }
        });
        data.products = Array.from(unique.values());
        return data;
      } catch (error) {
        console.error('[REV] Kits: dados inválidos da etapa.', error);
        return { products: [] };
      }
    }

    getProductGroupKey(products) {
      return (products || [])
        .map((product) => String(product?.id || ''))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        .join('|');
    }

    stabilizeProductOrder(products) {
      if (!Array.isArray(products) || products.length < 2) return products || [];

      const groupKey = this.getProductGroupKey(products);
      if (!groupKey) return products;

      if (!this.productOrderByGroup.has(groupKey)) {
        this.productOrderByGroup.set(
          groupKey,
          products.map((product) => String(product.id))
        );
        return products;
      }

      const masterOrder = this.productOrderByGroup.get(groupKey);
      const position = new Map(masterOrder.map((id, index) => [String(id), index]));

      return [...products].sort((a, b) => {
        const aPosition = position.has(String(a.id)) ? position.get(String(a.id)) : Number.MAX_SAFE_INTEGER;
        const bPosition = position.has(String(b.id)) ? position.get(String(b.id)) : Number.MAX_SAFE_INTEGER;

        if (aPosition !== bPosition) return aPosition - bPosition;
        return String(a.id).localeCompare(String(b.id), undefined, { numeric: true });
      });
    }

    initializeStep(step, index) {
      step._kitData = this.parseStepData(step);
      step._kitData.products = this.stabilizeProductOrder(step._kitData.products);
      step._state = {
        productId: String(step.dataset.baseProductId || step._kitData.baseProductId || ''),
        size: null,
        variant: null
      };

      const product = this.getSelectedProduct(step) || step._kitData.products[0];
      if (product) {
        step._state.productId = String(product.id);
        step._state.size = this.getInitialSize(product);
      }

      this.renderColors(step);
      this.renderSizes(step);
      this.updateStep(step);

      step.querySelector('[data-kit-colors]')?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-kit-color-product]');
        if (!button || button.classList.contains('is-unavailable')) return;

        const previousSize = step._state.size;
        step._state.productId = String(button.dataset.kitColorProduct);
        const nextProduct = this.getSelectedProduct(step);

        if (nextProduct) {
          const sizeIndex = this.getSizeOptionIndex(nextProduct);
          const preserved = previousSize && this.isSizeAvailable(nextProduct, sizeIndex, previousSize);
          step._state.size = preserved ? previousSize : this.getInitialSize(nextProduct);
        }

        this.renderColors(step);
        this.renderSizes(step);
        this.updateStep(step);
        this.updateKitPricing();
        this.galleryWasSynced = true;
        this.syncProductGallery(step);
        this.clearMessage(step);
      });

      step.querySelector('[data-kit-sizes]')?.addEventListener('change', (event) => {
        const input = event.target.closest('[data-kit-size]');
        if (!input || input.dataset.available !== 'true') return;

        step._state.size = input.value;
        this.updateStep(step);
        this.updateKitPricing();
        this.clearMessage(step);
      });

      step.querySelector('[data-kit-sizes]')?.addEventListener('click', (event) => {
        const label = event.target.closest('label[aria-disabled="true"]');
        if (label) event.preventDefault();
      });

      step.dataset.ready = 'true';
      step.dataset.index = String(index);
    }

    getSelectedProduct(step) {
      return (step._kitData?.products || []).find((product) => String(product.id) === String(step._state?.productId));
    }

    getSizeOptionIndex(product) {
      const options = product?.options || [];
      let index = options.findIndex((option) => this.sizeLabels.includes(this.normalize(option.name)));

      if (index === -1 && options.length === 1) {
        const onlyName = this.normalize(options[0]?.name);
        if (onlyName && onlyName !== 'title' && onlyName !== 'titulo') index = 0;
      }

      return index;
    }

    getVariantOption(variant, index) {
      if (!variant || index < 0) return null;
      return Array.isArray(variant.options) ? variant.options[index] : null;
    }

    isSizeAvailable(product, sizeIndex, size) {
      if (sizeIndex < 0) return true;
      return (product?.variants || []).some((variant) => {
        return variant.available && String(this.getVariantOption(variant, sizeIndex)) === String(size);
      });
    }

    getInitialSize(product) {
      const sizeIndex = this.getSizeOptionIndex(product);
      if (sizeIndex < 0) return null;

      const optionValues = product?.options?.[sizeIndex]?.values || [];
      const firstAvailable = optionValues.find((value) => this.isSizeAvailable(product, sizeIndex, value));
      return firstAvailable || optionValues[0] || null;
    }

    getSelectedVariant(step) {
      const product = this.getSelectedProduct(step);
      if (!product) return null;

      const variants = product.variants || [];
      const sizeIndex = this.getSizeOptionIndex(product);

      if (sizeIndex < 0) {
        return variants.find((variant) => variant.available) || variants[0] || null;
      }

      if (!step._state.size) return null;

      return variants.find((variant) => {
        return variant.available && String(this.getVariantOption(variant, sizeIndex)) === String(step._state.size);
      }) || variants.find((variant) => String(this.getVariantOption(variant, sizeIndex)) === String(step._state.size)) || null;
    }

    renderColors(step) {
      const wrapper = step.querySelector('[data-kit-colors-wrapper]');
      const container = step.querySelector('[data-kit-colors]');
      if (!wrapper || !container) return;

      const products = step._kitData?.products || [];
      if (!products.length) {
        wrapper.hidden = true;
        return;
      }

      wrapper.hidden = false;
      container.innerHTML = '';

      products.forEach((product) => {
        const hasAvailableVariant = (product.variants || []).some((variant) => variant.available);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ecomhard-kits__color-option';
        button.dataset.kitColorProduct = String(product.id);
        button.title = product.colorName || product.title || '';
        button.setAttribute('aria-label', product.colorName || product.title || 'Cor');
        button.setAttribute('aria-pressed', String(String(product.id) === String(step._state.productId)));

        if (String(product.id) === String(step._state.productId)) button.classList.add('is-selected');
        if (!hasAvailableVariant) {
          button.classList.add('is-unavailable');
          button.setAttribute('aria-disabled', 'true');
        }

        if (product.featuredImage) {
          const img = document.createElement('img');
          img.src = product.featuredImage;
          img.alt = product.colorName || product.title || '';
          img.loading = 'lazy';
          button.appendChild(img);
        } else {
          const placeholder = document.createElement('span');
          placeholder.className = 'ecomhard-kits__color-placeholder';
          button.appendChild(placeholder);
        }

        container.appendChild(button);
      });
    }

    renderSizes(step) {
      const wrapper = step.querySelector('[data-kit-sizes-wrapper]');
      const container = step.querySelector('[data-kit-sizes]');
      if (!wrapper || !container) return;

      const product = this.getSelectedProduct(step);
      const sizeIndex = this.getSizeOptionIndex(product);
      const values = sizeIndex >= 0 ? (product?.options?.[sizeIndex]?.values || []) : [];

      if (sizeIndex < 0 || !values.length) {
        wrapper.hidden = true;
        container.innerHTML = '';
        return;
      }

      wrapper.hidden = false;
      container.innerHTML = '';

      values.forEach((value, valueIndex) => {
        const available = this.isSizeAvailable(product, sizeIndex, value);
        const id = `${this.id}-step-${step.dataset.stepIndex}-size-${valueIndex}`;

        const input = document.createElement('input');
        input.className = 'sr-only';
        input.type = 'radio';
        input.name = `${this.id}-step-${step.dataset.stepIndex}-size`;
        input.id = id;
        input.value = value;
        input.dataset.kitSize = '';
        input.dataset.available = String(available);
        input.checked = String(step._state.size) === String(value);

        const label = document.createElement('label');
        label.className = `block-swatch${available ? '' : ' is-disabled'}`;
        label.htmlFor = id;
        label.textContent = value;

        if (!available) {
          label.setAttribute('aria-disabled', 'true');
          label.title = 'Indisponível';
        }

        container.append(input, label);
      });
    }

    updateStep(step) {
      const product = this.getSelectedProduct(step);
      const variant = this.getSelectedVariant(step);
      step._state.variant = variant;

      if (!product) return;

      const title = step.querySelector('[data-kit-product-title]');
      const image = step.querySelector('.ecomhard-kits__summary-image');
      const selection = step.querySelector('[data-kit-selection]');

      if (title) title.textContent = product.title || '';

      if (image) {
        const nextImage = variant?.image || product.featuredImage;

        if (nextImage) {
          if (image.tagName === 'IMG') {
            // O image_tag do Shopify gera srcset. Se alterarmos apenas o src,
            // o navegador pode continuar escolhendo a imagem antiga pelo srcset.
            // Removemos as fontes responsivas antigas antes de trocar a imagem.
            image.removeAttribute('srcset');
            image.removeAttribute('sizes');
            image.src = nextImage;
            image.alt = product.title || '';
          } else {
            const nextImg = document.createElement('img');
            nextImg.className = 'ecomhard-kits__summary-image';
            nextImg.src = nextImage;
            nextImg.alt = product.title || '';
            nextImg.loading = 'lazy';
            image.replaceWith(nextImg);
          }
        }
      }

      const parts = [];
      if (product.colorName) parts.push(`Cor: ${product.colorName}`);
      if (step._state.size) parts.push(`Tamanho: ${step._state.size}`);
      if (selection) selection.textContent = parts.join(' • ');

      this.updatePrice(step, variant);
    }

    getMainProductGallery() {
      const productRoot = this.closest('.product');
      return productRoot?.querySelector(':scope > product-gallery') || document.querySelector('.shopify-section--main-product product-gallery');
    }

    buildGalleryUrl(product) {
      const url = new URL(product.url, window.location.origin);
      if (this.sectionId) url.searchParams.set('section_id', this.sectionId);
      return url.toString();
    }

    async fetchGalleryMarkup(product) {
      const cacheKey = String(product.id);
      if (this.galleryCache.has(cacheKey)) return this.galleryCache.get(cacheKey);

      const galleryPromise = (async () => {
        const urls = [this.buildGalleryUrl(product)];

        // Fallback para a página completa caso a Section Rendering API não retorne a galeria.
        const fullPageUrl = new URL(product.url, window.location.origin).toString();
        if (!urls.includes(fullPageUrl)) urls.push(fullPageUrl);

        for (const url of urls) {
          try {
            const response = await fetch(url, {
              headers: { 'X-Requested-With': 'XMLHttpRequest' }
            });

            if (!response.ok) continue;

            const html = await response.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const gallery = doc.querySelector('.shopify-section--main-product product-gallery, product-gallery');

            if (gallery) return gallery.outerHTML;
          } catch (error) {
            console.warn('[REV] Kits: não foi possível carregar a galeria da cor selecionada.', error);
          }
        }

        return null;
      })();

      this.galleryCache.set(cacheKey, galleryPromise);
      const markup = await galleryPromise;
      if (!markup) this.galleryCache.delete(cacheKey);
      return markup;
    }

    async syncProductGallery(step) {
      const product = this.getSelectedProduct(step);
      if (!product?.url) return;

      const currentGallery = this.getMainProductGallery();
      if (!currentGallery) return;

      const requestId = ++this.galleryRequestId;
      let replaced = false;
      currentGallery.setAttribute('aria-busy', 'true');

      try {
        const markup = await this.fetchGalleryMarkup(product);
        if (!markup || requestId !== this.galleryRequestId) return;

        const doc = new DOMParser().parseFromString(markup, 'text/html');
        const nextGallery = doc.querySelector('product-gallery');
        if (!nextGallery) return;

        // O produto relacionado possui outro form_id. Mantemos o form da PDP atual
        // para que o componente nativo Prestige continue conectado aos eventos da página.
        const currentFormId = currentGallery.getAttribute('form');
        if (currentFormId) nextGallery.setAttribute('form', currentFormId);

        nextGallery.removeAttribute('aria-busy');
        currentGallery.replaceWith(nextGallery);
        replaced = true;
      } catch (error) {
        console.warn('[REV] Kits: erro ao trocar a galeria do produto.', error);
      } finally {
        if (!replaced && requestId === this.galleryRequestId && currentGallery.isConnected) {
          currentGallery.removeAttribute('aria-busy');
        }
      }
    }

    getDiscount(step, basePrice) {
      const type = this.normalize(step.dataset.discountType);
      const value = this.parseNumber(step.dataset.discountValue);
      let discounted = basePrice;

      if (value > 0 && type.includes('porcent')) {
        discounted = Math.round(basePrice * Math.max(0, 1 - value / 100));
      } else if (value > 0 && type.includes('fix')) {
        discounted = Math.max(0, basePrice - Math.round(value * 100));
      }

      return {
        type: step.dataset.discountType || '',
        value: step.dataset.discountValue || '',
        price: discounted,
        active: discounted < basePrice
      };
    }

    updatePrice(step, variant) {
      const priceEl = step.querySelector('[data-kit-price]');
      const compareEl = step.querySelector('[data-kit-compare-price]');
      if (!priceEl || !compareEl || !variant) return;

      const basePrice = Number(variant.price || 0);
      const discount = this.getDiscount(step, basePrice);

      if (discount.active) {
        compareEl.textContent = this.formatMoney(basePrice);
        compareEl.hidden = false;
        priceEl.textContent = this.formatMoney(discount.price);
      } else if (Number(variant.compareAtPrice || 0) > basePrice) {
        compareEl.textContent = this.formatMoney(Number(variant.compareAtPrice));
        compareEl.hidden = false;
        priceEl.textContent = this.formatMoney(basePrice);
      } else {
        compareEl.hidden = true;
        compareEl.textContent = '';
        priceEl.textContent = this.formatMoney(basePrice);
      }
    }

    updateKitPricing() {
      if (!this.pricing || !this.steps.length) return;

      let regularTotal = 0;
      let kitTotal = 0;

      this.steps.forEach((step) => {
        const variant = this.getSelectedVariant(step);
        if (!variant) return;

        const basePrice = Number(variant.price || 0);
        const discount = this.getDiscount(step, basePrice);
        regularTotal += basePrice;
        kitTotal += Number(discount.price || basePrice);
      });

      const savings = Math.max(0, regularTotal - kitTotal);
      const discountPercent = regularTotal > 0 && savings > 0
        ? Math.round((savings / regularTotal) * 100)
        : 0;

      const compareEl = this.pricing.querySelector('[data-kit-total-compare]');
      const priceEl = this.pricing.querySelector('[data-kit-total-price]');
      const helperEl = this.pricing.querySelector('[data-kit-total-helper]');
      const badgeEl = this.pricing.querySelector('[data-kit-total-discount-badge]');

      if (priceEl) priceEl.textContent = this.formatMoney(kitTotal);

      if (compareEl) {
        compareEl.textContent = this.formatMoney(regularTotal);
        compareEl.hidden = savings <= 0;
      }

      if (badgeEl) {
        badgeEl.textContent = discountPercent > 0 ? `${discountPercent}% OFF` : '';
        badgeEl.hidden = discountPercent <= 0;
      }

      if (helperEl) {
        if (savings <= 0) {
          helperEl.textContent = '';
          helperEl.hidden = true;
        } else if (this.pricing.dataset.helperMode === 'unit') {
          helperEl.textContent = `Cada peça por ${this.formatMoney(Math.round(kitTotal / this.steps.length))}`;
          helperEl.hidden = false;
        } else {
          helperEl.textContent = `Economizará ${this.formatMoney(savings)}`;
          helperEl.hidden = false;
        }
      }
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'pt-BR', {
          style: 'currency',
          currency: this.currency
        }).format(amount);
      } catch (_) {
        return `R$ ${amount.toFixed(2).replace('.', ',')}`;
      }
    }

    isStepValid(step) {
      const product = this.getSelectedProduct(step);
      const variant = this.getSelectedVariant(step);
      if (!product || !variant || !variant.available) return false;

      const sizeIndex = this.getSizeOptionIndex(product);
      if (sizeIndex >= 0 && !step._state.size) return false;

      return true;
    }

    showMessage(step, message, isError = false) {
      const el = step.querySelector('[data-kit-message]');
      if (!el) return;
      el.textContent = message || '';
      el.classList.toggle('is-error', Boolean(isError));
    }

    clearMessage(step) {
      this.showMessage(step, '', false);
    }

    validateStep(step) {
      if (this.isStepValid(step)) {
        this.clearMessage(step);
        return true;
      }

      this.showMessage(step, this.dataset.errorSize || 'Selecione um tamanho disponível.', true);
      return false;
    }

    bindNavigation() {
      this.addEventListener('click', (event) => {
        const next = event.target.closest('[data-kit-next]');
        const prev = event.target.closest('[data-kit-prev]');
        const addAll = event.target.closest('[data-kit-add-all]');

        if (next) {
          const step = next.closest('[data-kit-step]');
          if (!this.validateStep(step)) return;
          this.showStep(Math.min(this.currentStep + 1, this.steps.length - 1));
        }

        if (prev) {
          this.showStep(Math.max(this.currentStep - 1, 0));
        }

        if (addAll) {
          this.addAllToCart(addAll);
        }
      });
    }

    showStep(index, scroll = true) {
      if (!this.steps.length) return;

      const nextIndex = Math.max(0, Math.min(index, this.steps.length - 1));
      const currentIndex = this.currentStep;

      const scrollIfNeeded = () => {
        if (!scroll) return;
        const rect = this.getBoundingClientRect();
        if (rect.top < 0 || rect.top > window.innerHeight * 0.7) {
          this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      };

      if (nextIndex === currentIndex) {
        this.steps.forEach((step, stepIndex) => {
          const active = stepIndex === nextIndex;
          step.hidden = !active;
          step.classList.toggle('is-hidden', !active);
        });
        scrollIfNeeded();
        return;
      }

      if (this.isTransitioning) return;

      const currentStep = this.steps[currentIndex];
      const nextStep = this.steps[nextIndex];
      const direction = nextIndex > currentIndex ? 'forward' : 'backward';
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

      this.currentStep = nextIndex;

      const finishSwap = () => {
        currentStep.hidden = true;
        currentStep.classList.add('is-hidden');
        currentStep.classList.remove('is-leaving-forward', 'is-leaving-backward');

        nextStep.hidden = false;
        nextStep.classList.remove('is-hidden');

        if (this.galleryWasSynced) {
          this.syncProductGallery(nextStep);
        }

        scrollIfNeeded();
      };

      if (reduceMotion) {
        finishSwap();
        return;
      }

      this.isTransitioning = true;
      currentStep.classList.add(`is-leaving-${direction}`);

      window.setTimeout(() => {
        finishSwap();
        nextStep.classList.add(`is-entering-${direction}`);

        window.setTimeout(() => {
          nextStep.classList.remove('is-entering-forward', 'is-entering-backward');
          this.isTransitioning = false;
        }, 220);
      }, 170);
    }

    buildCartItems() {
      return this.steps.map((step, index) => {
        const product = this.getSelectedProduct(step);
        const variant = this.getSelectedVariant(step);
        if (!product || !variant) return null;

        const discount = this.getDiscount(step, Number(variant.price || 0));

        return {
          id: Number(variant.id),
          quantity: 1,
          properties: {
            _rev_kit_id: this.dataset.kitId || '',
            _rev_kit_title: this.dataset.kitTitle || '',
            _rev_kit_item_id: step.dataset.kitItemId || '',
            _rev_kit_step: `${index + 1}/${this.steps.length}`,
            _rev_kit_discount_type: discount.type,
            _rev_kit_discount_value: discount.value,
            _rev_kit_display_price: String(discount.price)
          }
        };
      }).filter(Boolean);
    }

    async addAllToCart(button) {
      const invalidIndex = this.steps.findIndex((step) => !this.isStepValid(step));
      if (invalidIndex !== -1) {
        this.showStep(invalidIndex);
        this.validateStep(this.steps[invalidIndex]);
        return;
      }

      if (button.getAttribute('aria-busy') === 'true') return;
      button.setAttribute('aria-busy', 'true');
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      try {
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections }
        }));

        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: this.buildCartItems(),
            sections: sections.join(','),
            sections_url: window.location.pathname
          })
        });

        const responseJson = await response.json();
        if (!response.ok) {
          throw new Error(responseJson.description || responseJson.message || this.dataset.errorCart || 'Não foi possível adicionar o kit.');
        }

        if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
          window.location.href = `${Shopify.routes.root}cart`;
          return;
        }

        const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cart.sections = responseJson.sections || {};

        this.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: responseJson.items || [],
            cart
          }
        }));

        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            cart
          }
        }));
      } catch (error) {
        const step = this.steps[this.currentStep];
        this.showMessage(step, error.message || this.dataset.errorCart || 'Não foi possível adicionar o kit.', true);
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } finally {
        button.removeAttribute('aria-busy');
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }
    }
  }

  window.customElements.define('ecomhard-kits', EcomhardKits);
})();
